<template>
  <div class="p-4">
    <div class="mb-6 flex items-center justify-between">
      <h2 class="text-xl font-bold text-gray-900 dark:text-white">
        Google Accounts
      </h2>
      <a
        href="/google/auth/google_oauth2/initiate"
        class="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 dark:bg-blue-600 dark:hover:bg-blue-700 focus:outline-none dark:focus:ring-blue-800">
        <svg class="w-4 h-4 me-2 -ms-1 inline-block" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM13.5 11h-2v2a.5.5 0 0 1-1 0v-2h-2a.5.5 0 0 1 0-1h2V8a.5.5 0 0 1 1 0v2h2a.5.5 0 0 1 0 1Z" />
        </svg>
        Connect Account
      </a>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center py-12">
      <div role="status">
        <svg aria-hidden="true" class="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
          <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
        </svg>
        <span class="sr-only">Loading...</span>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else-if="accounts.length === 0" class="p-12 text-center">
      <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
      <h3 class="mt-3 text-sm font-medium text-gray-900 dark:text-white">
        No accounts connected
      </h3>
      <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
        Connect your Google account to access Analytics and Search Console data.
      </p>
    </div>

    <!-- Accounts List -->
    <div v-else class="space-y-4">
      <div
        v-for="account in accounts"
        :key="account.id"
        class="bg-white border border-gray-200 rounded-lg shadow-sm dark:bg-gray-800 dark:border-gray-700">

        <!-- Account Header -->
        <div class="p-6">
          <div class="flex items-start justify-between">
            <div class="flex items-center">
              <img
                v-if="account.avatar_url"
                :src="account.avatar_url"
                :alt="account.name"
                class="w-10 h-10 rounded-full">
              <div v-else class="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center dark:bg-gray-700">
                <svg class="w-6 h-6 text-gray-400 dark:text-gray-500" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd" />
                </svg>
              </div>
              <div class="ms-4">
                <h3 class="text-base font-semibold text-gray-900 dark:text-white">
                  {{ account.name || account.email }}
                </h3>
                <p class="text-sm text-gray-500 dark:text-gray-400">
                  {{ account.email }}
                </p>
              </div>
            </div>

            <div class="flex items-center space-x-2">
              <button
                v-if="account.is_alive"
                @click="handleRefresh(account)"
                :disabled="refreshing === account.id"
                type="button"
                class="text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-gray-200 rounded-lg border border-gray-200 text-sm p-2.5 hover:text-gray-900 focus:z-10 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-500 dark:hover:text-white dark:hover:bg-gray-600 dark:focus:ring-gray-600 disabled:opacity-50">
                <svg class="w-4 h-4" :class="{ 'animate-spin': refreshing === account.id }" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span class="sr-only">Refresh</span>
              </button>

              <button
                @click="handleDisconnect(account)"
                :disabled="disconnecting === account.id"
                type="button"
                class="text-red-700 hover:text-white border border-red-700 hover:bg-red-800 focus:ring-4 focus:outline-none focus:ring-red-300 font-medium rounded-lg text-sm px-4 py-2 text-center dark:border-red-500 dark:text-red-500 dark:hover:text-white dark:hover:bg-red-600 dark:focus:ring-red-900 disabled:opacity-50">
                <span v-if="disconnecting === account.id">Disconnecting...</span>
                <span v-else>Disconnect</span>
              </button>
            </div>
          </div>

          <!-- Connection Status -->
          <div v-if="!account.is_alive" class="mt-4 p-4 text-sm text-yellow-800 rounded-lg bg-yellow-50 dark:bg-gray-700 dark:text-yellow-300" role="alert">
            <span class="font-medium">Connection expired</span> — Please reconnect this account to continue accessing data.
          </div>

          <!-- Stats -->
          <div class="mt-6 grid grid-cols-2 gap-4">
            <!-- Analytics -->
            <button
              @click="toggleAnalytics(account)"
              type="button"
              class="p-4 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600 transition-colors">
              <div class="flex items-center justify-between">
                <div class="text-left">
                  <p class="text-xs font-medium text-gray-500 uppercase dark:text-gray-400">
                    Analytics
                  </p>
                  <p class="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                    {{ account.analytics_accounts_count || 0 }}
                  </p>
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    Properties
                  </p>
                </div>
                <svg
                  class="w-5 h-5 text-gray-400 transition-transform"
                  :class="{ 'rotate-180': expandedAnalytics[account.id] }"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>

            <!-- Search Console -->
            <button
              @click="toggleSearchConsole(account)"
              type="button"
              class="p-4 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600 transition-colors">
              <div class="flex items-center justify-between">
                <div class="text-left">
                  <p class="text-xs font-medium text-gray-500 uppercase dark:text-gray-400">
                    Search Console
                  </p>
                  <p class="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                    {{ account.search_console_sites_count || 0 }}
                  </p>
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    Sites
                  </p>
                </div>
                <svg
                  class="w-5 h-5 text-gray-400 transition-transform"
                  :class="{ 'rotate-180': expandedSearchConsole[account.id] }"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>
          </div>
        </div>

        <!-- Analytics Properties List -->
        <div v-if="expandedAnalytics[account.id]" class="border-t border-gray-200 dark:border-gray-700">
          <div class="p-6">
            <h4 class="text-sm font-medium text-gray-900 dark:text-white mb-3">
              Analytics Properties
            </h4>
            <div v-if="loadingAnalytics[account.id]" class="flex justify-center py-4">
              <div role="status">
                <svg aria-hidden="true" class="w-6 h-6 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                  <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                </svg>
                <span class="sr-only">Loading...</span>
              </div>
            </div>
            <div v-else-if="analyticsProperties[account.id]?.length > 0" class="space-y-2">
              <div
                v-for="property in analyticsProperties[account.id]"
                :key="property.id"
                class="flex items-center p-3 text-sm text-gray-700 border border-gray-200 rounded-lg dark:text-gray-300 dark:border-gray-600">
                <svg class="w-4 h-4 me-3 flex-shrink-0 text-gray-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                </svg>
                {{ property.name || property.id }}
              </div>
            </div>
            <div v-else class="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
              No analytics properties found
            </div>
          </div>
        </div>

        <!-- Search Console Sites List -->
        <div v-if="expandedSearchConsole[account.id]" class="border-t border-gray-200 dark:border-gray-700">
          <div class="p-6">
            <h4 class="text-sm font-medium text-gray-900 dark:text-white mb-3">
              Search Console Sites
            </h4>
            <div v-if="loadingSearchConsole[account.id]" class="flex justify-center py-4">
              <div role="status">
                <svg aria-hidden="true" class="w-6 h-6 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                  <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                </svg>
                <span class="sr-only">Loading...</span>
              </div>
            </div>
            <div v-else-if="searchConsoleSites[account.id]?.length > 0" class="space-y-2">
              <div
                v-for="site in searchConsoleSites[account.id]"
                :key="site.id"
                class="flex items-center p-3 text-sm text-gray-700 border border-gray-200 rounded-lg dark:text-gray-300 dark:border-gray-600">
                <svg class="w-4 h-4 me-3 flex-shrink-0 text-gray-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" d="M4.083 9h1.946c.089-1.546.383-2.97.837-4.118A6.004 6.004 0 004.083 9zM10 2a8 8 0 100 16 8 8 0 000-16zM9.954 4.569c-1.084 1.379-1.656 2.924-1.784 4.431H11.83c-.128-1.507-.7-3.052-1.784-4.431A6.008 6.008 0 0010 4.52c-.015.016-.03.033-.046.049zm.046 11.911c.015-.016.03-.032.046-.049 1.084-1.379 1.656-2.924 1.784-4.431H8.17c.128 1.507.7 3.052 1.784 4.431.015.016.03.033.046.049zM12.823 9h2.074a6.004 6.004 0 01-2.783 4.118c.454-1.147.748-2.572.837-4.118zm-.837-5.118c1.134.936 2.17 2.518 2.783 4.118h-2.074c-.089-1.546-.383-2.97-.837-4.118zM5.166 13.118c.615 1.601 1.65 2.684 2.783 3.118a9.988 9.988 0 01-.837-4.118H5.103c.206.607.463 1.182.766 1.717a5.98 5.98 0 01-.703-1.717z" clip-rule="evenodd" />
                </svg>
                {{ site.url || site.site_url }}
              </div>
            </div>
            <div v-else class="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
              No search console sites found
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, reactive } from 'vue'
import { getGoogleAccounts, deleteGoogleAccount, refreshGoogleAccount, getAnalyticsProperties, getSearchConsoleSites } from '~/javascript/api/google_accounts'

// State
const loading = ref(true)
const accounts = ref([])
const disconnecting = ref(null)
const refreshing = ref(null)

// Expanded states
const expandedAnalytics = reactive({})
const expandedSearchConsole = reactive({})
const loadingAnalytics = reactive({})
const loadingSearchConsole = reactive({})
const analyticsProperties = reactive({})
const searchConsoleSites = reactive({})

// Methods
const fetchAccounts = async() => {
  try {
    const response = await getGoogleAccounts()
    accounts.value = response.data
  } catch (error) {
    console.error('Failed to fetch Google accounts:', error)
  } finally {
    loading.value = false
  }
}

const handleRefresh = async(account) => {
  refreshing.value = account.id

  try {
    await refreshGoogleAccount(account.id)
    // Refresh the accounts list to get updated counts
    await fetchAccounts()
  } catch (error) {
    console.error('Failed to refresh account:', error)
    alert('Failed to refresh account. Please try again.')
  } finally {
    refreshing.value = null
  }
}

const handleDisconnect = async(account) => {
  if (!confirm(`Are you sure you want to disconnect ${account.email}?`)) {
    return
  }

  disconnecting.value = account.id

  try {
    await deleteGoogleAccount(account.id)
    accounts.value = accounts.value.filter((a) => a.id !== account.id)
    // Clean up related data
    delete analyticsProperties[account.id]
    delete searchConsoleSites[account.id]
    delete expandedAnalytics[account.id]
    delete expandedSearchConsole[account.id]
  } catch (error) {
    console.error('Failed to disconnect account:', error)
    alert('Failed to disconnect account. Please try again.')
  } finally {
    disconnecting.value = null
  }
}

const toggleAnalytics = async(account) => {
  expandedAnalytics[account.id] = !expandedAnalytics[account.id]

  // Load analytics properties if expanding and not already loaded
  if (expandedAnalytics[account.id] && !analyticsProperties[account.id]) {
    loadingAnalytics[account.id] = true
    try {
      const response = await getAnalyticsProperties(account.id)
      analyticsProperties[account.id] = response.data || []
    } catch (error) {
      console.error('Failed to load analytics properties:', error)
      analyticsProperties[account.id] = []
    } finally {
      loadingAnalytics[account.id] = false
    }
  }
}

const toggleSearchConsole = async(account) => {
  expandedSearchConsole[account.id] = !expandedSearchConsole[account.id]

  // Load search console sites if expanding and not already loaded
  if (expandedSearchConsole[account.id] && !searchConsoleSites[account.id]) {
    loadingSearchConsole[account.id] = true
    try {
      const response = await getSearchConsoleSites(account.id)
      searchConsoleSites[account.id] = response.data || []
    } catch (error) {
      console.error('Failed to load search console sites:', error)
      searchConsoleSites[account.id] = []
    } finally {
      loadingSearchConsole[account.id] = false
    }
  }
}

const _formatDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

// Lifecycle
onMounted(() => {
  fetchAccounts()
})
</script>
